import * as _ from 'underscore';

import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Module } from '../Module';

// urlNamespace must conform to the regex used by getSanitizedUrlNamespace in SiteService.java
export let SiteUrlNameCapturingRegex = /^\/site\/([-_a-zA-Z0-9]+)/;

/**
 * A wrapper around UI-Router's UrlMatcher. UI-Router doesn't have built-in functionality
 * to support site prefixes in the way that we'd like, so we need this custom UrlMatcher.
 *
 * This enables:
 *
 *     $state.go('sitestate', {siteUrlName: 'blah'}) to create url '/site/blah/...'
 *     url '/site/blah/...' to make $stateParams.siteUrlName === 'blah'
 *
 * For more detailed documentation on this class and its methods, see:
 * http://angular-ui.github.io/ui-router/site/#/api/ui.router.util.type:UrlMatcher
 */
export class SiteUrlMatcher implements ng.ui.IUrlMatcher {

  constructor(private matcher: ng.ui.IUrlMatcher) {}

  /**
   * Add a pattern to the end of this pattern, returning a new SiteUrlMatcher
   */
  public concat(pattern: string): SiteUrlMatcher {
    return new SiteUrlMatcher(this.matcher.concat(pattern));
  }

  /**
   * Extracts site into siteUrlName if the site is present
   */
  public exec(path: string, searchParams: Object): {} {
    let siteUrlName = '';

    const match = path.match(SiteUrlNameCapturingRegex);

    // If site is present, extract it
    if (match) {
      siteUrlName = match[1];

      // Get rest of the string without the site part
      path = path.split(match[0])[1];
    }

    const urlParams: any = this.matcher.exec(path, searchParams);
    if(urlParams !== null) {
      urlParams[UrlParamKeys.SiteUrlName] = siteUrlName;
    }
    return urlParams;
  }

  /**
   * Return unsorted array of parameter names
   */
  public parameters(): string[] {
    const params = this.matcher.parameters();
    params.push(UrlParamKeys.SiteUrlName);
    return params;
  }

  /**
   * Create a url with the site, if a site is given
   */
  public format(values: any): string {
    let prefix = '';

    // If a truthy value, add to site prefix
    if (values[UrlParamKeys.SiteUrlName]) {
      prefix = '/site/' + values[UrlParamKeys.SiteUrlName];
    }

    // Remove property so matcher.format doesn't add a query string param
    if (_.has(values, UrlParamKeys.SiteUrlName)) {
      values = angular.copy(values);
      delete values[UrlParamKeys.SiteUrlName];
    }

    return prefix + this.matcher.format(values);
  }

}

/**
 * Can be used in config block or by other providers under the
 * name "SiteUrlMatcherFactoryProvider".
 */
export class SiteUrlMatcherFactory implements ng.IServiceProvider {

  public static $inject = ['$urlMatcherFactoryProvider'];

  constructor(private $urlMatcherFactory: ng.ui.IUrlMatcherFactory) {}

  public compile(pattern: string): SiteUrlMatcher {
    return new SiteUrlMatcher(this.$urlMatcherFactory.compile(pattern));
  }

  public $get(): SiteUrlMatcherFactory {
    return this;
  }

}

// Register as a provider to be available to other providers.
Module.instance().provider('SiteUrlMatcherFactory', SiteUrlMatcherFactory);
